/**
 * Dutch (nl) Organization JSON-LD. Edit `ORGANIZATION_NL_STATIC` for fixed fields; reviews come from the home reviews block.
 * @see https://developers.google.com/search/docs/appearance/structured-data/organization
 */
import type {
	OrganizationReviewBuildInput,
	OrganizationReviewInput
} from '$lib/seo/schemas/organizationReviews';
import type { Page } from '$models/Page/Page';
import type { Site } from '$models/Site/Site';
import { jsonLdScriptTag } from '$lib/seo/jsonLd';
import {
	findReviewsBlockOnLandingPage,
	mapReviewsBlockToOrganizationInputs,
	mergeOrganizationReviewsIntoSchema
} from '$lib/seo/schemas/organizationReviews';

export type { OrganizationReviewInput };
export type BuildOrganizationNlInput = OrganizationReviewBuildInput;

export { findReviewsBlockOnLandingPage, mapReviewsBlockToOrganizationInputs };

const ORGANIZATION_NL_STATIC: Record<string, unknown> = {
	'@context': 'https://schema.org',
	'@type': 'Organization',
	name: 'De Koffiejongens',
	alternateName: 'DKJ',
	url: 'https://dekoffiejongens.nl',
	logo: 'https://cdn.cms.thesubscriptioncompany.nl/wp-content/uploads/sites/5/2026/05/20124214/Logo-De-Koffiejongens.png',
	description:
		'De Koffiejongens levert 100% biologische specialty koffie in bonen én cups, zonder compromis op smaak. Onze duurzame cups zijn gemaakt van planten en thuis composteerbaar. Al onze pakketjes worden ingepakt door mensen met een afstand tot de arbeidsmarkt. Met onze handige bezorgservice kies je voor eerlijke handel en koffie die de wereld een stukje beter maakt.',
	foundingDate: '2018',
	email: 'info@dekoffiejongens.nl',
	vatID: 'NL856914241B01',
	contactPoint: {
		'@type': 'ContactPoint',
		contactType: 'customer service',
		email: 'info@dekoffiejongens.nl',
		telephone: '+31 85 208 3525',
		availableLanguage: ['Dutch', 'English', 'German']
	},
	sameAs: [
		'https://x.com/dekoffiejongens',
		'https://www.facebook.com/Dekoffiejongens/',
		'https://www.instagram.com/dekoffiejongens/',
		'https://www.linkedin.com/company/de-koffiejongens/',
		'https://www.tiktok.com/@dekoffiejongens'
	],
	address: {
		'@type': 'PostalAddress',
		streetAddress: 'Brediusweg 25',
		addressLocality: 'Bussum',
		postalCode: '1401 AB',
		addressCountry: 'NL'
	},
	numberOfEmployees: {
		'@type': 'QuantitativeValue',
		minValue: 10,
		maxValue: 49
	}
};

export function buildOrganizationNlSchema(
	input: OrganizationReviewBuildInput
): Record<string, unknown> {
	const schema: Record<string, unknown> = { ...ORGANIZATION_NL_STATIC };
	mergeOrganizationReviewsIntoSchema(schema, input);
	return schema;
}

/** Gates for injecting Organization JSON-LD on the Dutch homepage (`CmsPageShell`). */
export const ORGANIZATION_NL_INJECT = {
	pathname: '/',
	pageSlug: 'home',
	siteName: 'De Koffiejongens',
	language: 'nl'
} as const;

export type OrganizationNlHeadContext = {
	url: Pick<URL, 'pathname' | 'origin'>;
	page: Page;
	site: Pick<Site, 'name' | 'locale'>;
};

export function organizationNlHeadScriptHtml(context: OrganizationNlHeadContext): string {
	const g = ORGANIZATION_NL_INJECT;
	if (context.url.pathname !== g.pathname) {
		return '';
	}
	if (
		context.site.locale.language !== g.language ||
		context.site.name !== g.siteName ||
		context.page.slug !== g.pageSlug
	) {
		return '';
	}
	const reviewsBlock = findReviewsBlockOnLandingPage(context.page);
	const fromReviews = reviewsBlock ? mapReviewsBlockToOrganizationInputs(reviewsBlock) : null;

	return jsonLdScriptTag(
		buildOrganizationNlSchema({
			aggregateRating: fromReviews?.aggregateRating ?? null,
			reviews: fromReviews?.reviews?.length ? fromReviews.reviews : null
		})
	);
}
